import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight as ArrowRightIcon, Check } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import TableGuideLogo from "@/components/brand/TableGuideLogo";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";


import { COMIC_COLORS, COMIC_FONT } from "@/components/comic/comic-utils";
import {
  Trophy, PiggyBank, CalendarDays, MessageCircle,
  Users, Megaphone, Heart, Star, Utensils, Gift,
  Sparkles, Clock, MapPin,
} from "lucide-react";

const FEATURES = [
  {
    color: COMIC_COLORS.yellow,
    title: "Loyalty",
    subtitle: "Punkte & Benefits",
    lines: ["Jeder Besuch zählt", "Steig im Status auf", "Löse Rewards ein"],
    icons: [Trophy, Star, Gift],
    spreadX: -630,
    rotation: -5,
    y: 8,
    backStory: "Du gehst in dein Lieblingsrestaurant oder entdeckst neue Spots? Bei jedem Besuch sammelst du echtes Guthaben — und löst es ein, wann immer du willst. Dein Genuss wird belohnt. 🎁",
    backEmoji: "🏆",
    navigateTo: "/guest-dashboard?section=loyalty",
  },
  {
    color: COMIC_COLORS.cyan,
    title: "Ersparnisse",
    subtitle: "Beim Restaurantbesuch",
    lines: ["Exklusive Deals sichern", "Echt Geld sparen", "Mehr Genuss für weniger"],
    icons: [PiggyBank, Sparkles, Star],
    spreadX: -420,
    rotation: -3,
    y: 3,
    backStory: "Sieh auf einen Blick, in welchem Restaurant du wie viel gespart hast. Deine persönliche Spar-Übersicht — transparent, motivierend und immer aktuell. 💰",
    backEmoji: "📊",
    navigateTo: "/guest-dashboard?section=savings",
  },
  {
    color: COMIC_COLORS.orange,
    title: "Mein Plan",
    subtitle: "Kalender & Aktivitäten",
    lines: ["Alle Reservierungen", "Nächste Events im Blick", "Dein Food-Kalender"],
    icons: [CalendarDays, Clock, MapPin],
    spreadX: -210,
    rotation: -1,
    y: 0,
    backStory: "Dein Kalender, deine Reservierungen — alles an einem Ort. Lade Termine direkt in deinen Outlook- oder Google-Kalender. Nie wieder einen Tisch vergessen. 📅",
    backEmoji: "🗓️",
    navigateTo: "/guest-dashboard?section=bookings",
  },
  {
    color: COMIC_COLORS.pink,
    title: "Buchungen",
    subtitle: "Alles im Blick",
    lines: ["Chat mit Restaurants", "Reservierungen managen", "Dein persönlicher Planer"],
    icons: [MessageCircle, CalendarDays, Utensils],
    spreadX: 0,
    rotation: 0,
    y: -1,
    backStory: "Schluss mit Anrufen! Chatte direkt mit dem Restaurant, ändere deine Reservierung, passe Zeiten an oder storniere — alles in Sekunden, alles in deiner Hand. ✨",
    backEmoji: "💬",
    navigateTo: "/guest-dashboard?section=messages",
  },
  {
    color: COMIC_COLORS.teal,
    title: "Food Groups",
    subtitle: "Gemeinsam genießen",
    lines: ["Lunch mit Kollegen", "Dinner mit Freunden", "Essen auf Dienstreisen"],
    icons: [Users, MapPin, Utensils],
    spreadX: 210,
    rotation: 1,
    y: 0,
    backStory: "Ihr seid zu fünft und jeder isst anders? Kein Problem! Tippt einfach eure Wünsche rein — wir finden Restaurants, die perfekt zu euch allen passen. 🤝",
    backEmoji: "👥",
    navigateTo: "/guest-dashboard?section=food-groups",
  },
  {
    color: COMIC_COLORS.purple,
    title: "Food Board",
    subtitle: "Angebote kommen zu dir",
    lines: ["Poste deine Wünsche", "Restaurants bieten dir an", "Events · Feiern · Genuss"],
    icons: [Megaphone, Sparkles, Gift],
    spreadX: 420,
    rotation: 3,
    y: 3,
    backStory: "Panikattacke wegen der nächsten Feier? Brauchst du nicht! Schreib einfach, wie viele Gäste kommen und was ihr mögt — Restaurants kommen mit Deals zu dir. 🎉",
    backEmoji: "📢",
    navigateTo: "/guest-dashboard?section=food-board",
  },
  {
    color: COMIC_COLORS.red,
    title: "Family & Friends",
    subtitle: "Dein Kreis, dein Vorteil",
    lines: ["Freunde einladen", "Gemeinsam Punkte sammeln", "Exklusive Kreis-Rewards"],
    icons: [Heart, Star, Users],
    spreadX: 630,
    rotation: 5,
    y: 8,
    backStory: "Bei Groß werden lohnt sich's für beide Seiten! Empfiehl uns weiter und du bekommst ein echtes Dankeschön von uns. Teilen macht glücklich. ❤️",
    backEmoji: "🤗",
    navigateTo: "/guest-dashboard?section=invite",
  },
];

function SpeechBubble({ feature, counterRotation = 0 }: { feature: (typeof FEATURES)[0]; counterRotation?: number }) {
  // Compensate for parent rotation shifting the bubble sideways
  // Card height ~300px, shift = height * sin(rotation) 
  const xCompensation = Math.round(350 * Math.sin((counterRotation * Math.PI) / 180));
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.5, y: 8 }}
      transition={{ type: "spring", damping: 18, stiffness: 280 }}
      className="absolute z-[200] pointer-events-none"
      style={{
        left: "50%",
        bottom: "calc(100% + 20px)",
        transform: `translateX(calc(-50% + ${xCompensation}px)) rotate(${counterRotation}deg)`,
      }}
    >
      <div className="relative" style={{ width: "550px", height: "320px" }}>
        <svg
          viewBox="0 0 380 220"
          className="absolute inset-0 w-full h-full"
          style={{ filter: "drop-shadow(4px 5px 0 rgba(0,0,0,0.7))" }}
        >
          {/* Classic oval speech bubble with pointed tail */}
          <path
            d={`M190,15 
              C310,15 365,50 365,100 
              C365,150 310,185 190,185 
              C145,185 110,178 85,170 
              L${50 - xCompensation * 0.7},215 
              L${75 - xCompensation * 0.2},165 
              C40,150 15,130 15,100 
              C15,50 70,15 190,15 Z`}
            fill="white"
            stroke="black"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
        {/* Content inside bubble */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-20 py-12"
          style={{ fontFamily: COMIC_FONT }}
        >
          <span className="text-5xl mb-2">{feature.backEmoji}</span>
          <h4 className="font-black text-center text-2xl mb-2 text-black" style={{ letterSpacing: "0.03em" }}>
            {feature.title}
          </h4>
          <p className="text-[17px] text-black/85 text-center leading-relaxed font-semibold">
            {feature.backStory}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function WidgetCard({
  feature,
  index,
  isMobile,
}: {
  feature: (typeof FEATURES)[0];
  index: number;
  isMobile: boolean;
}) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const w = isMobile ? "155px" : "clamp(165px, 14vw, 190px)";
  const cardHeight = isMobile ? "210px" : "clamp(225px, 28vh, 300px)";

  const cardContent = (
    <div
      className="rounded-[24px] overflow-visible relative p-4 flex flex-col gap-3"
      style={{
        background: isHovered
          ? `linear-gradient(145deg, hsl(var(--card) / 0.96) 0%, hsl(var(--card) / 0.9) 100%)`
          : `linear-gradient(145deg, hsl(var(--card) / 0.92) 0%, hsl(var(--card) / 0.82) 100%)`,
        border: isHovered ? `2px solid ${feature.color}` : "1.5px solid hsl(var(--border) / 0.45)",
        boxShadow: isHovered
          ? `0 12px 28px hsl(228 35% 2% / 0.32), 0 0 0 1px ${feature.color}25`
          : "0 10px 24px hsl(228 35% 2% / 0.28), inset 0 1px 0 hsl(0 0% 100% / 0.06)",
        width: w,
        minHeight: cardHeight,
        transition: "border 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
        contain: "layout paint",
      }}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{
            background: `linear-gradient(135deg, ${feature.color}, ${feature.color}CC)`,
            boxShadow: `0 4px 16px ${feature.color}40`,
          }}
        >
          {(() => {
            const Icon = feature.icons[0];
            return <Icon className="w-4 h-4 text-black/80" />;
          })()}
        </div>
        <div>
          <h3
            className="text-white font-extrabold text-sm leading-tight"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            {feature.title}
          </h3>
          <p
            className="text-white/50 font-semibold leading-tight"
            style={{ fontSize: "0.6rem" }}
          >
            {feature.subtitle}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 mt-1">
        {feature.lines.map((line, li) => {
          const LineIcon = feature.icons[li] || feature.icons[0];
          return (
            <div
              key={li}
              className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
              style={{
                background: `linear-gradient(135deg, ${feature.color}14, ${feature.color}08)`,
                border: `1px solid ${feature.color}18`,
              }}
            >
              <LineIcon className="w-3.5 h-3.5 shrink-0" style={{ color: feature.color }} />
              <span
                className="text-white font-bold leading-tight"
                style={{ fontSize: "0.68rem", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
              >
                {line}
              </span>
            </div>
          );
        })}
      </div>

      {/* Small navigate button */}
      <button
        onClick={(e) => { e.stopPropagation(); navigate(feature.navigateTo); }}
        className="absolute bottom-2.5 right-2.5 flex items-center gap-1 px-2 py-1 rounded-lg text-black/80 font-bold z-10 hover:scale-105 active:scale-95 transition-transform"
        style={{
          fontSize: "0.55rem",
          background: `${feature.color}CC`,
          border: `1px solid ${feature.color}`,
          boxShadow: `1px 1px 0 rgba(0,0,0,0.5)`,
          fontFamily: COMIC_FONT,
          letterSpacing: "0.04em",
        }}
      >
        Öffnen
        <ArrowRightIcon className="w-2.5 h-2.5" />
      </button>

      <div
        className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 rounded-full blur-2xl pointer-events-none"
        style={{ background: feature.color, opacity: 0.1 }}
      />
    </div>
  );

  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.04 * index, duration: 0.32, type: "spring", damping: 24, stiffness: 160 }}
        className="shrink-0 snap-center relative"
        onClick={() => setIsHovered(!isHovered)}
      >
        <AnimatePresence>
          {isHovered && <SpeechBubble feature={feature} counterRotation={0} />}
        </AnimatePresence>
        {cardContent}
      </motion.div>
    );
  }

  return (
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{
        width: w,
        minHeight: cardHeight,
        zIndex: isHovered ? 50 : 7 - Math.abs(3 - index),
      }}
    >
      <motion.div
        initial={{ opacity: 0, x: 0, y: 0, scale: 0.85, rotateZ: 0 }}
        animate={{
          opacity: 1,
          x: feature.spreadX,
          y: feature.y,
          scale: 1,
          rotateZ: feature.rotation,
        }}
        transition={{
          delay: 0.05 * index,
          duration: 0.45,
          type: "spring",
          damping: 24,
          stiffness: 110,
          mass: 0.85,
        }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className="cursor-pointer relative"
        style={{ width: w, minHeight: cardHeight }}
      >
        <AnimatePresence>
          {isHovered && <SpeechBubble feature={feature} counterRotation={-feature.rotation} />}
        </AnimatePresence>
        {cardContent}
      </motion.div>
    </div>
  );
}

export default function ShowcaseLanding() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();
  const [dealCount, setDealCount] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Ensure autoplay works across browsers (muted + play fallback)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay);
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  // Live count of currently available deals (social proof)
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("deals")
      .select("id", { count: "exact", head: true })
      .eq("status", "ACTIVE")
      .gte("valid_until", new Date().toISOString())
      .then(({ count }) => {
        if (!cancelled && typeof count === "number" && count > 0) setDealCount(count);
      });
    return () => { cancelled = true; };
  }, []);

  // Force dark mode on this landing page, restore on unmount
  useEffect(() => {
    const prev = theme;
    if (theme !== "dark") setTheme("dark");
    return () => {
      // Only restore if user had explicitly chosen light before
      if (prev && prev !== "dark") setTheme(prev);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center overflow-hidden relative py-14 md:py-16"
      style={{
        background: `
          radial-gradient(ellipse 80% 60% at 50% 30%, hsl(225 40% 14%) 0%, transparent 70%),
          radial-gradient(ellipse 60% 50% at 80% 70%, hsl(260 30% 10%) 0%, transparent 60%),
          radial-gradient(ellipse 50% 40% at 20% 60%, hsl(200 30% 10%) 0%, transparent 60%),
          hsl(228 30% 5%)
        `,
      }}
    >
      {/* Cinematic marketing video background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/images/hero-watermark-logo.png"
        >
          <source src="/marketing-video.webm" type="video/webm" />
          <source src="/marketing-video.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay gradient for text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to bottom, hsl(228 40% 6% / 0.55) 0%, hsl(228 40% 6% / 0.35) 35%, hsl(228 40% 8% / 0.75) 100%),
              radial-gradient(ellipse 80% 60% at 50% 40%, hsl(228 40% 6% / 0.2) 0%, transparent 70%)
            `,
          }}
        />
      </div>

      {/* Ambient light effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute w-[600px] h-[600px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${COMIC_COLORS.cyan}22 0%, transparent 68%)`,
            top: "5%",
            left: "-10%",
          }}
        />
        <div
          className="absolute w-[500px] h-[500px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${COMIC_COLORS.purple}1C 0%, transparent 68%)`,
            bottom: "0%",
            right: "-5%",
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${COMIC_COLORS.orange}16 0%, transparent 68%)`,
            top: "40%",
            left: "40%",
          }}
        />
      </div>

      <div className="relative z-20 -mt-10 md:-mt-14 flex flex-col items-center text-center px-5 max-w-3xl">
        {/* Logo */}
        <motion.div
          className="flex justify-center -mb-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <TableGuideLogo className="h-24 md:h-32 lg:h-36 w-auto" position="hero" />
        </motion.div>

        {/* Value proposition */}
        <motion.h1
          className="text-white mt-5 text-balance"
          style={{
            fontFamily: "'Outfit', 'Figtree', system-ui, sans-serif",
            fontSize: "clamp(1.75rem, 4.2vw, 3rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
          }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
        >
          Bis zu 50&nbsp;% sparen in Restaurants in deiner Nähe
        </motion.h1>

        <motion.p
          className="text-white/70 mt-4 text-base md:text-lg font-medium max-w-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.24 }}
        >
          Exklusive Deals entdecken, kostenlos einen Tisch reservieren und direkt vor Ort einlösen.
          Für Gäste komplett kostenlos – keine Gebühren, kein Abo.
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="mt-7 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, type: "spring", damping: 20, stiffness: 180 }}
        >
          <button
            onClick={() => navigate("/home")}
            className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-semibold text-base bg-white text-[hsl(220_70%_16%)] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] hover:scale-[1.03] active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2"
          >
            Deals entdecken
            <ArrowRightIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate("/fuer-gastronomen")}
            className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-semibold text-base text-white border border-white/25 bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all"
          >
            Restaurant anmelden
          </button>
        </motion.div>

        {/* Trust row */}
        <motion.ul
          className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-white/60 text-[13px] font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.38 }}
        >
          {[
            dealCount !== null ? `${dealCount} aktive Deals` : "Täglich neue Deals",
            "0 € für Gäste",
            "Kostenlos reservieren",
            "Keine Kreditkarte nötig",
          ].map((item) => (
            <li key={item} className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-[hsl(160_60%_55%)]" />
              {item}
            </li>
          ))}
        </motion.ul>
      </div>

      {/* Feature overview */}
      <motion.p
        className="relative z-20 mt-10 md:mt-12 text-white/45 text-[11px] font-semibold uppercase tracking-[0.18em]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.46 }}
      >
        Alles, was dich erwartet
      </motion.p>

      {/* Cards container */}
      {isMobile ? (
        <div
          className="relative z-10 mt-5 w-full overflow-x-auto px-6 pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="flex gap-4 w-max px-2">
            {FEATURES.map((feature, i) => (
              <WidgetCard key={feature.title} feature={feature} index={i} isMobile />
            ))}
          </div>
        </div>
      ) : (
        <div
          className="relative z-30 mt-2 w-full flex items-center justify-center scale-[0.66] lg:scale-[0.72] xl:scale-[0.82] 2xl:scale-95"
          style={{ height: "clamp(280px, 33vh, 360px)", perspective: "1200px" }}
        >
          {FEATURES.map((feature, i) => (
            <WidgetCard key={feature.title} feature={feature} index={i} isMobile={false} />
          ))}
        </div>
      )}

      {/* Closing CTA */}
      <motion.button
        onClick={() => navigate("/home")}
        className="relative z-20 mb-10 mt-2 px-8 py-3 rounded-xl font-semibold text-sm text-white border border-white/25 bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all inline-flex items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
      >
        Jetzt Deals ansehen
        <ArrowRightIcon className="w-4 h-4" />
      </motion.button>
    </main>

  );
}
